import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { MapPin, Calendar, Clock, User, Phone, Ticket, ArrowLeft, Globe, Blend } from "lucide-react";
import { FORMAT_LABEL } from "@/components/events/EventFormatFields";
import ReportButton from "@/components/ReportButton";
import { FeatureButton } from "@/components/featured/FeatureButton";
import { FeaturedBadge } from "@/components/featured/FeaturedBadge";
import { RsvpBlock } from "@/components/events/RsvpBlock";
import { TicketPanel } from "@/components/events/TicketPanel";
import { FavoriteButton } from "@/components/FavoriteButton";
import { ShareButton, useTrackView } from "@/components/ShareButton";

export default function EventDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [e, setE] = useState(null);
  useEffect(() => { api.get(`/events/${id}`).then(r => setE(r.data)).catch(() => setE(false)); }, [id]);
  const [access, setAccess] = useState(null);
  const loadAccess = () => { if (user) api.get(`/events/${id}/access`).then(r => setAccess(r.data)).catch(() => setAccess(null)); };
  useEffect(() => { loadAccess(); }, [id, user]); // eslint-disable-line react-hooks/exhaustive-deps
  useTrackView("event", e ? e.id : null);

  if (e === null) return <div className="page pt-8 text-center text-zinc-500">A carregar…</div>;
  if (e === false) return <div className="page pt-8 text-center text-zinc-500">Evento não encontrado.</div>;

  const featuredActive = e.featured_paid && e.featured_until && new Date(e.featured_until) > new Date();

  return (
    <div className="page fade-up">
      <Link to="/eventos" data-testid="event-back" className="inline-flex items-center gap-1 text-sm text-zinc-500 py-3">
        <ArrowLeft size={14} /> {t("events")}
      </Link>
      <div className="rounded-2xl overflow-hidden bg-white border border-zinc-200">
        <div className="relative aspect-[16/9] bg-zinc-100">
          {e.image_url && <img src={e.image_url} alt={e.name} className="w-full h-full object-cover" />}
          <span className="absolute top-3 left-3 chip !bg-black !text-white !border-black">{e.category}</span>
          {featuredActive && <FeaturedBadge onDark className="absolute top-3 right-3" testid="event-featured-badge" />}
        </div>
        <div className="p-5">
          <h1 className="font-display text-2xl font-extrabold leading-tight mb-3 flex items-start justify-between gap-2"><span>{e.name}</span><span className="flex items-center gap-1 shrink-0"><ShareButton type="event" id={e.id} testid="event-share-btn" /><FavoriteButton type="event" id={e.id} count={e.favorites_count} size="lg" testid="event-fav-btn" /></span></h1>
          <div className="grid grid-cols-2 gap-3 text-xs mb-4">
            <Info icon={Calendar} label="Data" val={new Date(e.date).toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB")} />
            <Info icon={Clock} label="Hora" val={`${e.time}${e.timezone && e.timezone !== "Europe/London" ? ` (${e.timezone})` : ""}`} />
            <Info icon={e.format === "online" ? Globe : MapPin} label={e.format === "online" ? "Formato" : "Cidade"} val={e.format === "online" ? "Online" : e.city} />
            <Info icon={User} label="Organizador" val={e.organiser} />
          </div>
          {(e.format || "in_person") !== "in_person" && <div className="mb-3 text-xs" data-testid="event-format-badge"><span className="chip !text-[11px]">{e.format === "hybrid" ? <Blend size={11} className="inline mr-1" /> : <Globe size={11} className="inline mr-1" />}{FORMAT_LABEL[e.format]}{e.online_platform ? ` · ${e.online_platform}` : ""}</span></div>}
          <p className="text-sm text-zinc-700 leading-relaxed mb-4">{e.description}</p>
          {e.format !== "online" && <div className="text-xs text-zinc-500 mb-4" data-testid="event-venue"><MapPin size={11} className="inline" /> {e.venue}{e.address ? ` · ${e.address}` : ""}</div>}
          {(e.format || "in_person") !== "in_person" && (
            <div className="mb-4 rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-xs" data-testid="event-online-access">
              <div className="font-semibold mb-1 flex items-center gap-1"><Globe size={12} /> Acesso online{e.online_platform ? ` (${e.online_platform})` : ""}</div>
              {access?.online_link ? <a href={access.online_link} target="_blank" rel="noreferrer" data-testid="event-online-link" className="text-[#c8102e] font-semibold underline break-all">{access.online_link}</a>
                : access ? <span className="text-zinc-600" data-testid="event-online-link-pending">O link de acesso será disponibilizado mais perto da data do evento.{user && e.owner === user.id ? " Adiciona-o em «Editar» quando estiver pronto — os participantes confirmados são notificados por email." : " Receberás um email quando estiver disponível."}</span>
                : <span className="text-zinc-500">O link de acesso é enviado a quem tiver reserva confirmada (no bilhete e por email).</span>}
            </div>
          )}
          <TicketPanel eventId={e.id} onReserved={loadAccess} />
          <RsvpBlock event={e} onChange={r => setE(p => ({ ...p, going_count: r.going_count, interested_count: r.interested_count }))} />
          {user && e.owner === user.id && <Link to="/eventos/organizador" data-testid="event-manage-link" className="block text-center text-xs font-semibold underline mb-3">Gerir confirmações de presença</Link>}

          <div className="mb-3"><FeatureButton targetType="event" targetId={e.id} targetName={e.name} testid="event-feature-btn" /></div>

          <div className="flex gap-2">
            {e.ticket_link && (
              <a href={e.ticket_link} target="_blank" rel="noreferrer" data-testid="event-tickets-btn"
                 className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] text-white text-sm font-semibold transition-colors">
                <Ticket size={14} /> Bilhetes
              </a>
            )}
            {e.contact && (
              <a href={e.contact.startsWith("+") || /^\d/.test(e.contact) ? `tel:${e.contact}` : `mailto:${e.contact}`}
                 className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-black hover:bg-zinc-800 text-white text-sm font-semibold transition-colors">
                <Phone size={14} /> Contactar
              </a>
            )}
          </div>
          <div className="mt-4 pt-3 border-t border-zinc-100 flex justify-end">
            <ReportButton target_type="event" target_id={e.id} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ icon: I, label, val }) {
  return (
    <div className="bg-zinc-50 rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 flex items-center gap-1"><I size={11} /> {label}</div>
      <div className="font-semibold text-sm">{val}</div>
    </div>
  );
}
